'use client';
import type { ElementType } from 'react';
import { Button, DrawerHeader, DrawerTitle, Link, Skeleton } from '@skin-seoul/design-system';
import {
  TEMP_ITEM_KEY,
  useCartService,
  useRegionCurrency,
  type Currency,
  CLASSNAME,
  getFilteredHalloweenPromoItems,
  getCartSummary,
  inferIsFirstOrder,
} from '@skin-seoul/service';
import { BridgeApi, Condition, usePersonalizationTracker } from '@skin-seoul/utils';
import React, { useState } from 'react';
import { BMSMCampaignBanner } from '../../../bmsm-campaign/BMSMCampaignBanner';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import CartOrderSummary from '../components/CartOrderSummary';
import CartProductCard from '../components/CartProductCard';
import { CartTrustBadges } from '../components/CartTrustBadges';
import { LoyaltyPointsBannerContainer } from '../components/LoyaltyPointsBannerContainer';

type CartWithItemViewProps = {
  currency: Currency;
  onCheckoutClick?: () => void;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  linkComponent?: ElementType;
};

export const CartWithItemView = ({
  currency,
  onCheckoutClick,
  ImageComponent,
  linkComponent,
}: CartWithItemViewProps) => {
  const LinkComponent = linkComponent || Link;
  const { useGetCartSuspenseQuery, useDeleteCartItem, useUpdateCartItem } = useCartService();
  const { onCartClose } = useCartDrawer();

  const { data } = useGetCartSuspenseQuery({ currency });
  const { country } = useRegionCurrency();
  const cartData = data?.cart;
  const { mutate: deleteItemMutate } = useDeleteCartItem({ currency });
  const { mutate: updateItemMutate } = useUpdateCartItem({ currency });

  const tracker = usePersonalizationTracker();
  const shippingCost = cartData.shippingRates?.length > 0 ? cartData.shippingRates[0].price : 0;

  const { items, filteredCoupons, orderTotal, discountTotal, totalTax } = getCartSummary(cartData);

  const filteredItems = getFilteredHalloweenPromoItems(items);
  const totalProductPrice = filteredItems.reduce((prev, curr) => prev + curr.price.price * curr.quantity, 0);
  const totalProductQuantity = filteredItems.reduce((prev, curr) => prev + curr.quantity, 0);

  const [isDetailsVisible, setIsDetailsVisible] = useState(false);

  const handleDeleteItem = (itemKey: string, productId: number) => {
    tracker.removeFromCart(productId.toString(), 'cart');
    deleteItemMutate(
      {
        itemKey,
        currency,
      },
      {
        onSuccess: () => {
          if (BridgeApi.isWebview()) {
            BridgeApi.cartUpdated();
          }
        },
      },
    );
  };

  const handleUpdateCartItem = ({ quantity, itemKey }: { quantity: number; itemKey: string }) => {
    updateItemMutate(
      {
        itemKey,
        quantity,
        currency,
      },
      {
        onSuccess: () => {
          if (BridgeApi.isWebview()) {
            BridgeApi.cartUpdated();
          }
        },
      },
    );
  };

  return (
    <>
      <DrawerHeader className="items-center bg-generic-0 flex h-12 flex-row justify-between p-3 w-full pr-5">
        <DrawerTitle className="font-medium text-center text-label-large w-full">My Bag</DrawerTitle>
      </DrawerHeader>

      <div className="p-0 pb-3 w-full flex-1 overflow-y-auto">
        <div className="w-full mb-2">
          <LoyaltyPointsBannerContainer
            linkComponent={linkComponent}
            cartTotalPrice={totalProductPrice}
            country={country}
            currency={currency}
            isFirstOrder={inferIsFirstOrder(cartData.coupons ?? [])}
          />
        </div>
        <div className="flex flex-col w-full px-3">
          <QueryBoundary clientOnly loadingFallback={<Skeleton className="w-full h-[130px]" />}>
            <BMSMCampaignBanner />
          </QueryBoundary>
          {[...filteredItems].reverse().map((item, index) => (
            <React.Fragment key={item.key}>
              <Condition
                expression={item.name === TEMP_ITEM_KEY}
                then={<CartProductCard.Skeleton />}
                else={
                  <CartProductCard
                    item={item}
                    currency={cartData.currency.currency}
                    onQuantityChange={(quantity) => {
                      handleUpdateCartItem({
                        itemKey: item.key,
                        quantity: quantity,
                      });
                    }}
                    onRemoveItem={() => handleDeleteItem(item.key, item.productId)}
                    ImageComponent={ImageComponent}
                    linkComponent={linkComponent}
                  />
                }
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      {!isDetailsVisible && (
        <div className="w-full shrink-0 flex">
          <CartTrustBadges />
        </div>
      )}
      <div
        className="flex flex-col h-fit p-4 gap-3 w-full bg-generic-0"
        style={{ boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)' }}
      >
        <div className="relative flex flex-col gap-5">
          <CartOrderSummary
            subtotal={totalProductPrice ? totalProductPrice : 0}
            deliveryFee={shippingCost}
            totalPrice={orderTotal}
            totalTax={totalTax}
            country={country}
            coupons={filteredCoupons}
            isFreeShipping={shippingCost === 0}
            itemCount={totalProductQuantity}
            isDetailsVisible={isDetailsVisible}
            onDetailsVisibleChange={setIsDetailsVisible}
          />
        </div>

        <Button
          asChild
          size="lg"
          variant="primary"
          className={`w-full rounded-[8px] hover:bg-primary-400 active:bg-primary-400 ${CLASSNAME.NOT_TRANSLATE}`}
          onClick={() => {
            onCheckoutClick?.();
            onCartClose();
          }}
        >
          <LinkComponent href="/checkout/" className="no-underline hover:no-underline text-white">
            Proceed to Checkout
          </LinkComponent>
        </Button>
      </div>
    </>
  );
};
